import { calculatePlanPrice, processVehicleData } from '@/apis/product';
import { createPaProposal, retrievePaProposal } from '@/apis/proposal';
import { QuotationData } from '@/types/PlanSummary';
import { PlanSummaryState } from '@/types/PlanSummary/PlanSummaryState';
import { CalculatePlanPriceData } from '@/types/Proposal';
import { PaRetrieveProposalResponse } from '@/types/Proposal/PaRetrieveProposalResponse';
import { ProcessVehicleDataResponse } from '@/types/Proposal/ProcessVehicleDataResponse';
import { ApiResponseWrapper } from '@/types/Wrapper';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export const initialState: PlanSummaryState = {
  declaration: false,
  marketingConsent: false,
  payMode: '',
  quotationDetail: undefined,
  encodedOrderId: '',
  planPrice: undefined,
  loading: false,
  error: false,
  readyForPayment: false,
  processVehicle: undefined,
  agentCode: '',
};

const planSummarySlice = createSlice({
  name: 'planSummary',
  initialState,
  reducers: {
    resetPlanSummaryState: () => {
      return initialState;
    },
    updateDeclaration: (state, action: PayloadAction<boolean>) => {
      state.declaration = action.payload;
    },
    updateMarketingConsent: (state, action: PayloadAction<boolean>) => {
      state.marketingConsent = action.payload;
    },
    updatePayMode: (state, action: PayloadAction<string>) => {
      state.payMode = action.payload;
    },
    updateQuotationDetail: (state, action: PayloadAction<QuotationData>) => {
      state.quotationDetail = action.payload;
    },
    mapProposalDataToPlanSummary: (state, action: PayloadAction<PaRetrieveProposalResponse>) => {
      const data = action.payload;
      state.readyForPayment = true;
      state.quotationDetail = {
        planType: data.pa.planClass,
        planPremium: {
          discountRate: data.discountRate,
          discountAmount: data.discountAmount,
          taxRate: data.taxRate,
          taxAmount: data.taxAmount,
          stampDuty: data.stampDuty,
          netPremium: data.planPremium,
          feeAmount: data.feeAmount,
          grossPremium: data.premium,
          totalPremium: data.totalPremium,
          planClassId: data.proposalPa.planClass,
          sumAssured: data.proposalPa.sumAssured,
        },
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(processVehicleData.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(processVehicleData.fulfilled, (state, action) => {
        state.loading = true;
        const processVehicleResponse = action.payload.data as ProcessVehicleDataResponse;
        state.processVehicle = processVehicleResponse;
        state.loading = false;
        state.error = false;
      })
      .addCase(processVehicleData.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
    builder
      .addCase(calculatePlanPrice.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(calculatePlanPrice.fulfilled, (state, action) => {
        state.loading = true;
        const planPriceResponse = action.payload.data as CalculatePlanPriceData;
        state.planPrice = planPriceResponse ?? state.planPrice;
        state.loading = false;
        state.error = false;
      })
      .addCase(calculatePlanPrice.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder.addCase(createPaProposal.fulfilled, (state, action) => {
      state.encodedOrderId = action.payload.data;
      state.loading = false;
      state.error = false;
    });

    builder
      .addCase(retrievePaProposal.pending, (state) => {
        state.loading = true;
        state.proposalData = {
          message: '',
        } as ApiResponseWrapper<PaRetrieveProposalResponse>;
      })
      .addCase(retrievePaProposal.fulfilled, (state, action) => {
        state.loading = false;
        const proposalData = action.payload as ApiResponseWrapper<PaRetrieveProposalResponse>;
        state.proposalData = proposalData;

        if (proposalData.succeeded) state.encodedOrderId = proposalData.data.encodedOrderId;
      })
      .addCase(retrievePaProposal.rejected, (state, action) => {
        state.loading = false;
        state.proposalData = action.payload as ApiResponseWrapper<PaRetrieveProposalResponse>;
      });
  },
});

export const {
  resetPlanSummaryState,
  updateDeclaration,
  updateMarketingConsent,
  updatePayMode,
  updateQuotationDetail,
  mapProposalDataToPlanSummary,
} = planSummarySlice.actions;

export default planSummarySlice.reducer;
